import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import "../Style/Cg.css";
import { useLanguage } from "../Context/Languagecontext";

// ── Image imports ──
import labelAssistantImg from "../Assets/aa2.jpg";
import kkbLungisImg from "../Assets/legacy.webp";
import arunMillsImg from "../Assets/leader.jpeg";
import weavingUnitImg from "../Assets/legacy2.webp";

const TOTAL = 4;

function getOffset(index, activeIndex) {
  const raw = index - activeIndex;
  return raw < 0 ? raw + TOTAL : raw;
}

export default function Cg() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState("next");
  const [animating, setAnimating] = useState(false);
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);

  const { t, lang } = useLanguage();
  const cgData = t.careerGrowth;

  /* ── Intersection Observer: trigger scroll-in animations ── */
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(false);

          setTimeout(() => {
            setInView(true);
          }, 60);
        } else {
          setInView(false);
        }
      },
      { threshold: 0.18 }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  /* ── Localized Career Cards Mapping ── */
  const careerCards = useMemo(() => {
    return [
      {
        id: "01",
        year: "2000",
        eyebrow: cgData?.cards?.card1?.eyebrow || "Entering the Business World",
        title: cgData?.cards?.card1?.title || "From Label Assistant to Business Leader",
        image: labelAssistantImg,
        alt: "Mr. Arunkumar beginning his career in textile operations",
        description: cgData?.cards?.card1?.description || "",
        highlight: cgData?.cards?.card1?.highlight || "",
      },
      {
        id: "02",
        year: "KKB",
        eyebrow: cgData?.cards?.card2?.eyebrow || "Family Business, Earned Responsibility",
        title: cgData?.cards?.card2?.title || "Strengthening the Legacy of KKB Lungis",
        image: kkbLungisImg,
        alt: "KKB Lungis textile brand legacy",
        description: cgData?.cards?.card2?.description || "",
        highlight: cgData?.cards?.card2?.highlight || "",
      },
      {
        id: "03",
        year: "MD",
        eyebrow: cgData?.cards?.card3?.eyebrow || "Corporate Rise",
        title: cgData?.cards?.card3?.title || "A Managing Director Defined by Action",
        image: arunMillsImg,
        alt: "Arun Mills and textile leadership",
        description: cgData?.cards?.card3?.description || "",
        highlight: cgData?.cards?.card3?.highlight || "",
      },
      {
        id: "04",
        year: "Scale",
        eyebrow: cgData?.cards?.card4?.eyebrow || "Strategic Expansion",
        title: cgData?.cards?.card4?.title || "Expanding Tradition into a Larger Textile Vision",
        image: weavingUnitImg,
        alt: "Weaving and textile manufacturing units",
        description: cgData?.cards?.card4?.description || "",
        highlight: cgData?.cards?.card4?.highlight || "",
      },
    ];
  }, [cgData]);

  const go = useCallback(
    (nextIndex) => {
      if (animating || nextIndex === activeIndex) return;
      const goingForward =
        nextIndex > activeIndex ||
        (activeIndex === TOTAL - 1 && nextIndex === 0);
      setDirection(goingForward ? "next" : "prev");
      setAnimating(true);
      setActiveIndex(nextIndex);
      setTimeout(() => setAnimating(false), 560);
    },
    [activeIndex, animating]
  );

  const goNext = useCallback(() => go((activeIndex + 1) % TOTAL), [activeIndex, go]);
  const goPrev = useCallback(() => go((activeIndex - 1 + TOTAL) % TOTAL), [activeIndex, go]);

  const layeredCards = useMemo(
    () =>
      careerCards.map((card, index) => ({
        ...card,
        offset: getOffset(index, activeIndex),
      })),
    [activeIndex, careerCards]
  );

  return (
    <section
      className={`cg${inView ? " cg--visible" : ""}${lang === "ta" ? " cg--tamil" : ""}`}
      id="career-growth"
      ref={sectionRef}
    >
      <div className="cg__container">

        {/* ── Header ── */}
        <header className="cg__header">
          <div className="cg__kicker-row">
            <span className="cg__rule" />
            <span className="cg__kicker">{cgData?.kicker}</span>
            <span className="cg__rule" />
          </div>
          <h2 className="cg__title">
            {cgData?.title}
          </h2>
          <p className="cg__intro">
            {cgData?.intro}
          </p>
        </header>

        {/* ── Card stack (no shell wrapper) ── */}
        <div
          className={`cg__deck cg__deck--${direction}${animating ? " cg__deck--animating" : ""}`}
          aria-label={cgData?.controls?.navLabel || "Card navigation"}
        >
          {layeredCards.map((card, index) => (
            <article
              key={card.id}
              className={`cg__card cg__card--o${card.offset}`}
              onClick={() => card.offset !== 0 && go(index)}
              tabIndex={card.offset === 0 ? 0 : -1}
              onKeyDown={(e) => e.key === "Enter" && card.offset !== 0 && go(index)}
              role="button"
              aria-current={card.offset === 0 ? "true" : undefined}
              aria-label={card.offset !== 0 ? `${cgData?.controls?.viewLabel || "View"} ${card.eyebrow}` : undefined}
            >
              {/* Image */}
              <div className="cg__media">
                <img src={card.image} alt={card.alt} loading="lazy" />
                <div className="cg__media-grad" aria-hidden="true" />
                {/* Floating year badge over image */}
                <span className="cg__img-badge">{card.year}</span>
              </div>

              {/* Text */}
              <div className="cg__body">
                <span className="cg__eyebrow-label">{card.eyebrow}</span>
                <h3 className="cg__card-title">{card.title}</h3>
                <p className="cg__desc">{card.description}</p>
                <p className="cg__hl">{card.highlight}</p>
              </div>
            </article>
          ))}
        </div>

        {/* ── Controls ── */}
        <div className="cg__controls">
          <button
            type="button"
            className="cg__arrow cg__arrow--prev"
            onClick={goPrev}
            aria-label={cgData?.controls?.prevLabel || "Previous card"}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path d="M11 14L6 9L11 4" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          <nav className="cg__pips" aria-label={cgData?.controls?.navLabel || "Card navigation"}>
            {careerCards.map((card, index) => (
              <button
                key={card.id}
                type="button"
                className={`cg__pip${index === activeIndex ? " cg__pip--on" : ""}`}
                onClick={() => go(index)}
                aria-label={`${cgData?.controls?.goToLabel || "Go to"} ${card.eyebrow}`}
                aria-pressed={index === activeIndex}
              />
            ))}
          </nav>

          <button
            type="button"
            className="cg__arrow cg__arrow--next"
            onClick={goNext}
            aria-label={cgData?.controls?.nextLabel || "Next card"}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path d="M7 4L12 9L7 14" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Divider */}
        <div className="cg__divider" aria-hidden="true">
          <span /><span className="cg__gem">◆</span><span />
        </div>

      </div>
    </section>
  );
}